import React from "react";
import { useHistory } from "react-router-dom";
import { Typography } from "antd";
import styles from "./FoundingAccessPage.module.css";

const { Title, Text } = Typography;

/** Checkmark icon (inline SVG) for trust bullets */
function CheckIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M16.667 5L7.5 14.167 3.333 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** SVG shield badge with curved top, pointed bottom, glow; idSuffix ensures unique defs when multiple instances */
function GradeShield({ idSuffix }) {
  const fillId = `shieldFill-${idSuffix}`;
  const glossId = `shieldGloss-${idSuffix}`;
  const glowId = `shieldGlow-${idSuffix}`;
  return (
    <div className={styles.shieldWrap}>
      <svg className={styles.shieldSvg} viewBox="0 0 120 98" aria-hidden="true">
        <defs>
          <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#34D399" />
            <stop offset="100%" stopColor="#16A34A" />
          </linearGradient>
          <linearGradient id={glossId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(255,255,255,0.28)" />
            <stop offset="38%" stopColor="rgba(255,255,255,0.10)" />
            <stop offset="70%" stopColor="rgba(255,255,255,0.00)" />
          </linearGradient>
          <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feColorMatrix
              in="blur"
              type="matrix"
              values="0 0 0 0 0.203 0 0 0 0 0.827 0 0 0 0 0.604 0 0 0 0.9 0"
              result="greenBlur"
            />
            <feMerge>
              <feMergeNode in="greenBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path
          d="M14 14 Q60 2 106 14 L114 24 V56 Q114 64 108 70 L60 94 L12 70 Q6 64 6 56 V24 Z"
          fill={`url(#${fillId})`}
          filter={`url(#${glowId})`}
        />
        <path
          d="M18 18 Q60 8 102 18 L108 26 V55 Q108 61 104 66 L60 86 L16 66 Q12 61 12 55 V26 Z"
          fill="none"
          stroke="rgba(255,255,255,0.20)"
          strokeWidth="2"
        />
        <path
          d="M18 18 Q60 8 102 18 L108 26 V45 Q60 58 12 45 V26 Z"
          fill={`url(#${glossId})`}
          opacity="0.9"
        />
      </svg>
      <div className={styles.shieldLetter}>A</div>
    </div>
  );
}

import foundingCamryImage from "../../assets/images/founding-camry-listing.png";

export default function FoundingAccessPage() {
  const history = useHistory();

  const handleCta = () => {
    history.push("/onboarding?step=signup");
  };

  return (
    <div className={styles.page}>
      <div className={styles.bgOverlay} aria-hidden="true" />
      <div className={styles.vignette} aria-hidden="true" />

      <header className={styles.header}>
        <div className={styles.headerInner}>
          <div className={styles.logoRow}>
            <span className={styles.logoText}>Hooptie</span>
          </div>
        </div>
      </header>

      <main className={styles.main}>
        <div className={styles.container}>
          {/* Badge row */}
          <div className={styles.badgeRow}>
            <span className={`${styles.pill} ${styles.pillGreen}`}>7 Day Free Trial</span>
            <span className={styles.pill}>Founding Dealer Access</span>
            <span className={styles.pill}>Limited Spots</span>
          </div>

          <Title level={1} className={styles.h1}>
            Facebook Cars That Actually Flip
          </Title>
          <Text className={styles.subtext}>
            Private Facebook listings in your market scored for turn speed, demand, and estimated resale profit.
          </Text>

          {/* Example insight card */}
          <div className={styles.proofCard}>
            <div className={styles.cardLeft}>
              <div className={styles.cardTitleBlock}>
                <span className={styles.cardImageTitle}>2021 Toyota Camry SE</span>
                <span className={styles.cardImageLocation}>Houston, TX</span>
              </div>
              <div className={styles.gradeBadgeBlockMobile}>
                <div className={styles.gradeWrap}>
                  <div className={styles.gradeLabel}>Overall Letter Grade</div>
                  <GradeShield idSuffix="mobile" />
                </div>
              </div>
              <div className={styles.cardImageWrap}>
                <img src={foundingCamryImage} alt="2021 Toyota Camry SE" className={styles.cardImage} />
              </div>
            </div>
            <div className={styles.cardRight}>
              <div className={styles.gradeBadgeBlock}>
                <div className={styles.gradeWrap}>
                  <div className={styles.gradeLabel}>Overall Letter Grade</div>
                  <GradeShield idSuffix="desk" />
                </div>
              </div>
              <div className={styles.cardMetrics}>
                <div className={styles.metricRow}>
                  <span className={styles.metricLabel}>Active Listings:</span>
                  <span className={styles.metricValue}>15</span>
                </div>
                <div className={styles.metricRow}>
                  <span className={styles.metricLabel}>Sold (Past 90 Days):</span>
                  <span className={styles.metricValue}>28</span>
                </div>
                <div className={styles.metricRow}>
                  <span className={styles.metricLabel}>Avg. Days on Market:</span>
                  <span className={styles.metricValue}>29</span>
                </div>
                <div className={styles.metricRow}>
                  <span className={styles.metricLabel}>Avg. Retail Price:</span>
                  <span className={`${styles.metricValue} ${styles.metricValueSemiBold}`}>$22,000</span>
                </div>
                <div className={styles.metricRow}>
                  <span className={styles.metricLabel}>Estimated Profit:</span>
                  <span className={`${styles.metricValue} ${styles.metricValueYellow}`}>$2,800</span>
                </div>
                <div className={styles.buyButtonWrap}>
                  <div className={styles.buyButton}>BUY</div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <button type="button" className={styles.ctaButton} onClick={handleCta}>
            Start Smarter Sourcing for Free
          </button>

          {/* Trust bullets */}
          <div className={styles.trustRow}>
            <span className={styles.trustItem}>
              <CheckIcon />
              7 Day Free Trial
            </span>
            <span className={styles.trustItem}>
              <CheckIcon />
              Cancel Anytime
            </span>
            <span className={styles.trustItem}>
              <CheckIcon />
              Built for Independent Dealers
            </span>
          </div>
        </div>
      </main>
    </div>
  );
}
